// „Mein Konto“ → Zwei-Faktor-Anmeldung.
//
// Einrichten in drei Schritten: Passwort bestätigen, QR-Code scannen und mit
// einem Code bestätigen, dann die zehn Wiederherstellungscodes sichern – die
// gibt es genau dieses eine Mal im Klartext. Abschalten und neue Codes nur mit
// Passwort UND Code: Eine offen gelassene Sitzung reicht dafür nicht.

import { useEffect, useState, type FormEvent } from "react";
import { kontoApi, type ZweiFaktorStatus } from "../api";
import { Badge, Card, Skeleton } from "../components/ui";
import { CodeFeld } from "./CodeFeld";
import { kontoFehler } from "./fehler";

type Schritt =
  | { art: "ruhe" }
  | { art: "passwort" }
  | { art: "scannen"; secret: string; qr: string }
  | { art: "codes"; codes: string[]; neu: boolean }
  | { art: "bestaetigen"; zweck: "abschalten" | "neue-codes" };

const labelKlasse = "block text-[0.8rem] font-medium text-secondary";

export function ZweiFaktorKarte(): JSX.Element {
  const [status, setStatus] = useState<ZweiFaktorStatus | null>(null);
  const [schritt, setSchritt] = useState<Schritt>({ art: "ruhe" });
  const [passwort, setPasswort] = useState("");
  const [code, setCode] = useState("");
  const [wiederherstellung, setWiederherstellung] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notiert, setNotiert] = useState(false);
  const [hinweis, setHinweis] = useState<string | null>(null);

  const laden = (): void => {
    kontoApi.status().then(setStatus).catch((err) => setError(kontoFehler(err)));
  };
  useEffect(laden, []);

  const zuruecksetzen = (): void => {
    setSchritt({ art: "ruhe" });
    setPasswort("");
    setCode("");
    setError(null);
    setWiederherstellung(false);
    setNotiert(false);
  };

  const ausfuehren = async (f: () => Promise<void>): Promise<void> => {
    setError(null);
    setHinweis(null);
    setBusy(true);
    try {
      await f();
    } catch (err) {
      setError(kontoFehler(err, "Deine Anmeldung ist abgelaufen. Bitte neu anmelden."));
    } finally {
      setBusy(false);
    }
  };

  const einrichtenStarten = (e: FormEvent): Promise<void> => {
    e.preventDefault();
    return ausfuehren(async () => {
      const r = await kontoApi.einrichten(passwort);
      setPasswort("");
      setCode("");
      setSchritt({ art: "scannen", secret: r.secret, qr: r.qr_svg });
    });
  };

  const einschalten = (e: FormEvent): Promise<void> => {
    e.preventDefault();
    return ausfuehren(async () => {
      const r = await kontoApi.einschalten(code);
      setCode("");
      setSchritt({ art: "codes", codes: r.recovery_codes, neu: true });
      laden();
    });
  };

  const bestaetigen = (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (schritt.art !== "bestaetigen") return Promise.resolve();
    return ausfuehren(async () => {
      if (schritt.zweck === "abschalten") {
        await kontoApi.abschalten(passwort, code);
        zuruecksetzen();
        setHinweis("Die Zwei-Faktor-Anmeldung ist abgeschaltet. Du meldest dich jetzt nur noch mit dem Passwort an.");
      } else {
        const r = await kontoApi.neueCodes(passwort, code);
        setPasswort("");
        setCode("");
        setSchritt({ art: "codes", codes: r.recovery_codes, neu: false });
      }
      laden();
    });
  };

  const an = status?.enabled ?? false;

  return (
    <Card
      title="Zwei-Faktor-Anmeldung"
      description="Zusätzlich zum Passwort ein Code aus einer App auf deinem Handy – ein erratenes Passwort allein reicht dann nicht mehr."
      actions={status && (an ? <Badge tone="success" dot>An</Badge> : <Badge tone="neutral">Aus</Badge>)}
    >
      {!status && !error && <Skeleton className="h-16 w-full" />}
      {hinweis && <div className="callout callout-success mb-4" role="status">{hinweis}</div>}

      {/* --- Ruhezustand ------------------------------------------------ */}
      {status && schritt.art === "ruhe" && !an && (
        <div className="max-w-xl space-y-4">
          <p className="text-sm text-secondary">
            Du brauchst eine Authenticator-App, zum Beispiel <strong>Aegis</strong> oder <strong>2FAS</strong> (Android),
            <strong> Google Authenticator</strong>, <strong>Microsoft Authenticator</strong> oder den Passwortmanager deines Vertrauens.
          </p>
          <button type="button" className="btn btn-primary" onClick={() => { setError(null); setHinweis(null); setSchritt({ art: "passwort" }); }}>
            Einrichten
          </button>
        </div>
      )}
      {status && schritt.art === "ruhe" && an && (
        <div className="max-w-xl space-y-4">
          <div className="well space-y-1.5 text-sm">
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted">Eingeschaltet seit</span>
              <span className="font-medium text-primary">{status.enabled_at ? new Date(status.enabled_at).toLocaleDateString("de-DE", { day: "numeric", month: "long", year: "numeric" }) : "–"}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted">Wiederherstellungscodes übrig</span>
              <span className={`font-medium ${status.recovery_codes_left <= 3 ? "text-amber-600 dark:text-amber-400" : "text-primary"}`}>{status.recovery_codes_left} von 10</span>
            </div>
          </div>
          {status.recovery_codes_left <= 3 && (
            <div className="callout callout-warning">Nur noch wenige Wiederherstellungscodes. Lass dir neue geben, solange du die App noch hast.</div>
          )}
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn btn-secondary" onClick={() => { setError(null); setHinweis(null); setSchritt({ art: "bestaetigen", zweck: "neue-codes" }); }}>
              Neue Wiederherstellungscodes
            </button>
            <button type="button" className="btn btn-ghost text-red-600 dark:text-red-400" onClick={() => { setError(null); setHinweis(null); setSchritt({ art: "bestaetigen", zweck: "abschalten" }); }}>
              Abschalten
            </button>
          </div>
        </div>
      )}

      {/* --- 1. Passwort bestätigen ------------------------------------- */}
      {schritt.art === "passwort" && (
        <form onSubmit={einrichtenStarten} className="max-w-md space-y-4">
          <p className="text-sm text-secondary">Zur Sicherheit zuerst dein Passwort.</p>
          <label className={labelKlasse}>Passwort
            <input type="password" autoComplete="current-password" autoFocus required className="input mt-1.5" value={passwort} onChange={(e) => setPasswort(e.target.value)} />
          </label>
          <div className="flex gap-2">
            <button type="submit" disabled={busy} className="btn btn-primary">{busy ? "Moment …" : "Weiter"}</button>
            <button type="button" className="btn btn-ghost" onClick={zuruecksetzen}>Abbrechen</button>
          </div>
        </form>
      )}

      {/* --- 2. Scannen und bestätigen ---------------------------------- */}
      {schritt.art === "scannen" && (
        <form onSubmit={einschalten} className="space-y-5">
          <div className="grid items-start gap-5 sm:grid-cols-[auto_minmax(0,1fr)]">
            {/* Das SVG erzeugt die Suite selbst (shared/qr) – weißer Grund auch im Dunkelmodus, sonst liest die Kamera nichts. */}
            <div
              className="mx-auto w-[196px] rounded-xl p-2 shadow-sm ring-1 ring-black/5 [&>svg]:h-auto [&>svg]:w-full sm:mx-0"
              style={{ background: "#ffffff" }}
              aria-label="QR-Code für die Authenticator-App"
              role="img"
              dangerouslySetInnerHTML={{ __html: schritt.qr }}
            />
            <ol className="space-y-3 text-sm text-secondary">
              <li><span className="font-semibold text-primary">1.</span> In der App „Konto hinzufügen“ wählen und den QR-Code scannen.</li>
              <li>
                <span className="font-semibold text-primary">2.</span> Kein Scannen möglich? Diesen Schlüssel von Hand eintragen (zeitbasiert, 6 Ziffern):
                <code className="mt-1.5 block break-all rounded-md bg-input px-2.5 py-1.5 font-mono text-[0.8rem] tracking-wider text-primary ring-1 ring-inset ring-black/5 dark:ring-white/10">{schritt.secret}</code>
              </li>
              <li><span className="font-semibold text-primary">3.</span> Den Code, den die App jetzt zeigt, hier eingeben:</li>
            </ol>
          </div>
          <div className="max-w-xs">
            <CodeFeld value={code} onChange={setCode} autoFocus />
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={busy || code.length !== 6} className="btn btn-primary">{busy ? "Prüfe …" : "Einschalten"}</button>
            <button type="button" className="btn btn-ghost" onClick={zuruecksetzen}>Abbrechen</button>
          </div>
        </form>
      )}

      {/* --- 3. Wiederherstellungscodes --------------------------------- */}
      {schritt.art === "codes" && (
        <div className="max-w-xl space-y-4">
          <div className="callout callout-warning">
            <div>
              <div className="font-semibold">{schritt.neu ? "Eingeschaltet. Jetzt die Wiederherstellungscodes sichern!" : "Deine neuen Wiederherstellungscodes"}</div>
              <div className="mt-0.5 opacity-90">
                Handy verloren oder App gelöscht? Mit einem dieser Codes kommst du trotzdem hinein – jeder gilt einmal.
                Du siehst sie nur jetzt. {schritt.neu ? "" : "Die alten Codes gelten nicht mehr."}
              </div>
            </div>
          </div>
          <ul className="grid grid-cols-2 gap-2 rounded-lg border border-default p-3 font-mono text-[0.95rem] tracking-wider text-primary">
            {schritt.codes.map((c) => <li key={c} className="text-center">{c}</li>)}
          </ul>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn btn-secondary" onClick={() => void navigator.clipboard?.writeText(schritt.codes.join("\n")).then(() => setHinweis("In die Zwischenablage kopiert."))}>
              Kopieren
            </button>
            <a
              className="btn btn-secondary"
              download="wiederherstellungscodes.txt"
              href={`data:text/plain;charset=utf-8,${encodeURIComponent(`Wiederherstellungscodes (je einmal gültig)\n\n${schritt.codes.join("\n")}\n`)}`}
            >
              Als Textdatei speichern
            </a>
          </div>
          <label className="flex items-center gap-2 text-sm text-secondary">
            <input type="checkbox" checked={notiert} onChange={(e) => setNotiert(e.target.checked)} />
            Ich habe die Codes sicher aufbewahrt.
          </label>
          <button type="button" disabled={!notiert} className="btn btn-primary" onClick={() => { zuruecksetzen(); setHinweis(null); }}>
            Fertig
          </button>
        </div>
      )}

      {/* --- Abschalten / neue Codes: Passwort + Code ------------------- */}
      {schritt.art === "bestaetigen" && (
        <form onSubmit={bestaetigen} className="max-w-md space-y-4">
          <p className="text-sm text-secondary">
            {schritt.zweck === "abschalten"
              ? "Zum Abschalten bitte Passwort und einen aktuellen Code bestätigen."
              : "Für neue Codes bitte Passwort und einen aktuellen Code bestätigen. Die bisherigen Wiederherstellungscodes verfallen."}
          </p>
          <label className={labelKlasse}>Passwort
            <input type="password" autoComplete="current-password" autoFocus required className="input mt-1.5" value={passwort} onChange={(e) => setPasswort(e.target.value)} />
          </label>
          <div>
            <span className={labelKlasse}>{wiederherstellung ? "Wiederherstellungscode" : "Code aus der App"}</span>
            <div className="mt-1.5 max-w-xs">
              <CodeFeld key={wiederherstellung ? "r" : "t"} value={code} onChange={setCode} wiederherstellung={wiederherstellung} />
            </div>
            <button type="button" className="mt-1.5 text-xs font-medium text-brand-primary underline-offset-2 hover:underline" onClick={() => { setWiederherstellung(!wiederherstellung); setCode(""); }}>
              {wiederherstellung ? "Code aus der App verwenden" : "Stattdessen einen Wiederherstellungscode"}
            </button>
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={busy} className={schritt.zweck === "abschalten" ? "btn btn-danger" : "btn btn-primary"}>
              {busy ? "Moment …" : schritt.zweck === "abschalten" ? "Abschalten" : "Neue Codes erzeugen"}
            </button>
            <button type="button" className="btn btn-ghost" onClick={zuruecksetzen}>Abbrechen</button>
          </div>
        </form>
      )}

      {error && <div className="callout callout-danger mt-4" role="alert">{error}</div>}
    </Card>
  );
}
